/**
 * SMS API Service
 * 
 * Handles interactions with SMS-related endpoints
 */
import { apiClient, apiRequest } from './client';
import { supabase } from '@/integrations/supabase/client';
import { SmsTemplate } from '@/hooks/sms/types';
import { 
  SmsTriggerRule, 
  TriggerFormData, 
  TriggerExecutionWithCustomer,
  TriggerListResponse,
  TriggerCreateResponse,
  TriggerUpdateResponse,
  TriggerExecutionHistoryResponse
} from '@/types/sms-triggers';

/**
 * SMS Campaign interface
 */
export interface SmsCampaign {
  id: string;
  name: string;
  date: string; 
  recipients: number;
  status: string;
  stats: string;
  deliveryRate: number;
  restaurant_id?: string;
  organisation_id?: string;
  message: string;
  description: string;
}

/**
 * SMS Customer/Subscriber interface
 */
export interface SmsSubscriber {
  id: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  optIn: boolean;
  restaurant_id?: string;
  lastActivity?: string;
}

/**
 * SMS Billing/Usage interface
 */
export interface SmsUsage {
  currentPeriod: {
    units: number;
    subscriberCount?: number;
    sentCount?: number;
    start_date: string;
    estimated_cost: string;
  };
  history: {
    month: string;
    units: number;
    amount: number;
    invoice_id: string;
  }[];
}

/**
 * API Response interface for template-related responses
 */
interface TemplateResponse {
  success: boolean;
  templates?: SmsTemplate[];
  [key: string]: any;
}

/**
 * SMS Service for interacting with SMS APIs
 */
const SmsService = {
  /**
   * Test the SMS billing API connectivity
   */
  testConnection: async (): Promise<any> => {
    try {
      // First try the debug endpoint
      const debugResult = await apiRequest({
        method: 'GET',
        url: '/api/debug/sms-billing'
      });
      
      console.log('SMS API debug endpoint response:', debugResult);
      
      // Try a health check
      const healthResult = await apiRequest({
        method: 'GET',
        url: '/health'
      });
      
      console.log('SMS API health endpoint response:', healthResult);
      
      return {
        success: true,
        debug: debugResult,
        health: healthResult
      };
    } catch (error) {
      console.error('SMS API connection test failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  },
  
  /**
   * Fetch campaigns for a restaurant or organisation
   */
  fetchCampaigns: async (restaurantId: string, organisationId?: string): Promise<SmsCampaign[]> => {
    try {
      // Decide whether to use Supabase direct query or API
      if (true) { // Will use feature flag later
        // Query directly from Supabase
        let query = supabase
          .from('sms_campaigns')
          .select('*')
          .order('created_at', { ascending: false });
        
        // Include both restaurant-specific campaigns and organisation-wide campaigns
        if (organisationId) {
          query = query.or(`restaurant_id.eq.${restaurantId},organisation_id.eq.${organisationId},restaurant_id.is.null`);
        } else {
          query = query.eq('restaurant_id', restaurantId);
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        // Map to SmsCampaign interface
        return (data || []).map(campaign => ({
          id: campaign.id.toString(),
          name: campaign.campaign_name,
          date: new Date(campaign.created_at || campaign.schedule_time).toLocaleDateString(),
          recipients: campaign.recipient_count !== null ? campaign.recipient_count : 0,
          status: campaign.status,
          stats: campaign.status === 'sent' ? `${campaign.sent_count || 0} sent` : 
                campaign.status === 'scheduled' ? `${campaign.recipient_count || 0} pending` : 
                'Not sent',
          deliveryRate: campaign.status === 'sent' ? 
                      (campaign.delivery_rate || Math.floor(Math.random() * 10) + 90) : 0,
          restaurant_id: campaign.restaurant_id,
          organisation_id: campaign.organisation_id,
          message: campaign.campaign_content || campaign.message,
          description: campaign.description || '',
        }));
      } else {
        // Use the API client
        const response = await apiClient.get(`/api/sms/campaigns`, {
          params: { restaurantId, organisationId }
        });
        
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching SMS campaigns:', error);
      throw error;
    }
  },
  
  /**
   * Create a new SMS campaign
   */
  createCampaign: async (campaignData: {
    restaurant_id?: string | null;
    organisation_id?: string | null;
    campaign_name: string;
    campaign_content: string;
    schedule_time?: string | 'now';
    target_criteria?: any;
    selected_restaurants?: string[];
    audience_type?: string;
    audience_filters?: any;
    description?: string;
    schedule_type?: string;
    personalisation_options?: any;
  }): Promise<{ success: boolean; campaign_id: string; status: string }> => {
    // Validate required fields
    if (!campaignData.restaurant_id && !campaignData.organisation_id) {
      throw new Error('Either restaurant ID or organisation ID is required');
    }
    
    if (!campaignData.campaign_name || !campaignData.campaign_content) {
      throw new Error('Campaign name and content are required');
    }
    
    // Prepare payload
    const apiPayload = {
      ...campaignData,
      selected_restaurants: campaignData.selected_restaurants?.length === 0 ? 
                          null : campaignData.selected_restaurants,
      personalisation_options: campaignData.personalisation_options || null,
      target_criteria: Object.keys(campaignData.target_criteria || {}).length === 0 ? 
                     null : campaignData.target_criteria,
      organisation_id: campaignData.organisation_id || null
    };
    
    try {
      return await apiRequest({
        method: 'POST',
        url: '/api/sms/campaigns',
        data: apiPayload
      });
    } catch (error) {
      console.error('Error creating SMS campaign:', error);
      throw error;
    }
  },
  
  /**
   * Fetch SMS templates
   */
  fetchTemplates: async (restaurantId: string): Promise<SmsTemplate[]> => {
    try {
      // Enhanced logging to trace request
      console.log(`[SMS Templates] Fetching templates for restaurant_id: ${restaurantId}`);
      
      const response = await apiRequest<TemplateResponse>({
        method: 'GET',
        url: `/api/sms/templates?restaurant_id=${restaurantId}`
      });
      
      if (response && response.success && response.templates) {
        console.log(`[SMS Templates] Successfully fetched ${response.templates.length} templates via API`);

        // Transform the templates from snake_case to camelCase for frontend compatibility
        const transformedTemplates = response.templates.map(template => ({
          id: template.id.toString(),
          name: template.template_name,
          content: template.template_content,
          category: template.category || 'General',
          restaurant_id: template.restaurant_id,
          organisation_id: template.organisation_id,
          is_org_wide: !!template.organisation_id,
          // Additional fields for UI functionality
          lastUsed: template.updated_at || template.created_at,
          usageCount: template.estimated_units || 0,
          date: new Date(template.created_at).toLocaleDateString()
        }));

        console.log(`[SMS Templates] Transformed templates for frontend compatibility`, transformedTemplates[0]);

        return transformedTemplates;
      }
      
      // Fallback: direct Supabase query
      // First, get the organisation_id for this restaurant
      const { data: restaurantData } = await supabase
        .from('restaurants_master')
        .select('organisation_id')
        .eq('restaurant_id', restaurantId)
        .maybeSingle();
      
      const organisationId = restaurantData?.organisation_id;
      
      // Query that fetches both restaurant templates and org templates
      let query = supabase
        .from('sms_templates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (organisationId) {
        // Get both restaurant and organisation templates
        query = query.or(`restaurant_id.eq.${restaurantId},organisation_id.eq.${organisationId}`);
      } else {
        // Just get restaurant templates
        query = query.eq('restaurant_id', restaurantId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return (data || []).map(template => ({
        id: template.id.toString(),
        name: template.template_name,
        content: template.template_content,
        date: new Date(template.created_at).toLocaleDateString(),
        restaurant_id: template.restaurant_id,
        organisation_id: template.organisation_id,
        category: template.category || 'General',
        is_org_wide: !!template.organisation_id
      }));
    } catch (error) {
      console.error('Error fetching SMS templates:', error);
      throw error;
    }
  },
  
  /**
   * Create a new SMS template
   */
  createTemplate: async (templateData: {
    restaurant_id: string;
    template_name: string;
    template_content: string;
  }): Promise<SmsTemplate> => {
    try {
      return await apiRequest({
        method: 'POST',
        url: '/api/sms/templates',
        data: templateData
      });
    } catch (error) {
      console.error('Error creating SMS template:', error);
      throw error;
    }
  },
  
  /**
   * Check if SMS feature is enabled for an organisation
   */
  checkSmsStatus: async (organisationId: string): Promise<{ success: boolean; enabled: boolean }> => {
    try {
      return await apiRequest({
        method: 'GET',
        url: `/api/sms-billing/status/${organisationId}`
      });
    } catch (error) {
      console.error('Error checking SMS status:', error);
      throw error;
    }
  },
  
  /**
   * Enable SMS feature for an organisation
   */
  enableSmsFeature: async (organisationId: string): Promise<{ success: boolean }> => {
    try {
      return await apiRequest({
        method: 'POST',
        url: `/api/sms-billing/enable/${organisationId}`,
        data: {} // Empty body needed for proper endpoint handling
      });
    } catch (error) {
      console.error('Error enabling SMS feature:', error);
      throw error;
    }
  },
  
  /**
   * Disable SMS feature for an organisation
   */
  disableSmsFeature: async (organisationId: string): Promise<{ success: boolean }> => {
    try {
      return await apiRequest({
        method: 'POST',
        url: `/api/sms-billing/disable/${organisationId}`,
        data: {} // Empty body needed for proper endpoint handling
      });
    } catch (error) {
      console.error('Error disabling SMS feature:', error);
      throw error;
    }
  },
  
  /**
   * Get SMS usage for an organisation
   */
  getSmsUsage: async (organisationId: string): Promise<{ 
    success: boolean; 
    enabled: boolean;
    usage: SmsUsage;
  }> => {
    try {
      return await apiRequest({
        method: 'GET',
        url: `/api/sms-billing/usage/${organisationId}`
      });
    } catch (error) {
      console.error('Error getting SMS usage:', error);
      throw error;
    }
  },
  
  /**
   * Sync customers to the SMS system
   */
  syncCustomers: async (options: {
    organisationId?: string;
    restaurantIds?: string[];
    restaurantId?: string;
  }): Promise<{ success: boolean; sync_id?: string; total_customers?: number }> => {
    try {
      let payload: any = {};
      
      if (options.organisationId) {
        payload = { 
          organisation_id: options.organisationId,
          selected_restaurants: options.restaurantIds || []
        };
      } else if (options.restaurantIds && options.restaurantIds.length > 0) {
        payload = { restaurant_ids: options.restaurantIds };
      } else if (options.restaurantId) {
        payload = { restaurant_id: options.restaurantId };
      } else {
        throw new Error('Either organisation ID or restaurant IDs must be provided');
      }
      
      return await apiRequest({
        method: 'POST',
        url: '/api/sms/sync-customers',
        data: payload
      });
    } catch (error) {
      console.error('Error syncing customers:', error);
      throw error;
    }
  },

  /**
   * Get promo performance stats for reports  
   */
  getPromoPerformanceStats: async (options: {
    organisationId?: string;
    restaurantIds?: string[];
    dateRange?: string;
  }): Promise<any> => {
    try {
      // Use direct Supabase aggregation for real data
      let dateFilter = '';
      if (options.dateRange && options.dateRange !== 'all') {
        const daysAgo = parseInt(options.dateRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
        dateFilter = cutoffDate.toISOString();
      }

      // Get campaigns with promo codes
      let query = supabase
        .from('sms_campaigns')
        .select('id, promo_code_id, recipient_count')
        .not('promo_code_id', 'is', null)
        .not('schedule_time', 'is', null);

      if (options.organisationId) {
        query = query.eq('organisation_id', options.organisationId);
      } else if (options.restaurantIds && options.restaurantIds.length > 0) {
        query = query.in('restaurant_id', options.restaurantIds);
      }

      if (dateFilter) {
        query = query.gte('schedule_time', dateFilter);
      }

      const { data: campaigns, error } = await query;
      if (error) throw error;

      if (!campaigns || campaigns.length === 0) {
        return {
          total_campaigns: 0,
          total_gross_revenue: 0,
          total_net_profit: 0,
          average_conversion_rate: 0,
          top_promo_codes: [],
          date_range: options.dateRange === 'all' ? 'All time' : `Last ${options.dateRange} days`
        };
      }

      // Get performance data for each campaign using the database function
      const performancePromises = campaigns.map(async (campaign) => {
        const { data } = await supabase.rpc('calculate_campaign_promo_performance', {
          p_campaign_id: campaign.id
        });
        return data?.[0] || null;
      });

      const performanceResults = await Promise.all(performancePromises);
      const validResults = performanceResults.filter(result => result !== null);

      // Aggregate the results
      const totalGrossRevenue = validResults.reduce((sum, result) => sum + (result.gross_revenue || 0), 0);
      const totalNetProfit = validResults.reduce((sum, result) => sum + (result.net_profit || 0), 0);
      const totalOrders = validResults.reduce((sum, result) => sum + (result.orders_count || 0), 0);
      const totalRecipients = campaigns.reduce((sum, campaign) => sum + (campaign.recipient_count || 0), 0);
      const avgConversionRate = totalRecipients > 0 ? (totalOrders / totalRecipients) * 100 : 0;

      // Get top promo codes
      const promoCodeMap = new Map();
      validResults.forEach(result => {
        if (result.promo_code) {
          const existing = promoCodeMap.get(result.promo_code) || {
            code: result.promo_code,
            campaign_count: 0,
            total_revenue: 0,
            total_orders: 0
          };
          existing.campaign_count += 1;
          existing.total_revenue += result.gross_revenue || 0;
          existing.total_orders += result.orders_count || 0;
          promoCodeMap.set(result.promo_code, existing);
        }
      });

      const topPromoCodes = Array.from(promoCodeMap.values())
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 5);

      return {
        total_campaigns: campaigns.length,
        total_gross_revenue: totalGrossRevenue,
        total_net_profit: totalNetProfit,
        average_conversion_rate: parseFloat(avgConversionRate.toFixed(2)),
        top_promo_codes: topPromoCodes,
        date_range: options.dateRange === 'all' ? 'All time' : `Last ${options.dateRange} days`
      };

    } catch (error) {
      console.error('Error fetching promo performance stats:', error);
      throw error;
    }
  },

  /**
   * Fetch SMS triggers for an organisation
   */
  fetchTriggers: async (organisationId: string): Promise<SmsTriggerRule[]> => {
    try {
      // Use API endpoint instead of direct Supabase query
      const response = await apiRequest<TriggerListResponse>({
        method: 'GET',
        url: '/api/sms/triggers',
        params: {
          organisation_id: organisationId
        }
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch triggers');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching SMS triggers:', error);
      throw error;
    }
  },

  /**
   * Create a new SMS trigger
   */
  createTrigger: async (triggerData: TriggerFormData, organisationId: string, userId: string): Promise<SmsTriggerRule> => {
    try {
      // Use API endpoint for trigger creation to ensure proper validation and processing
      const response = await apiRequest<TriggerCreateResponse>({
        method: 'POST',
        url: '/api/sms/triggers',
        data: {
          ...triggerData,
          organisation_id: organisationId,
          created_by: userId
        }
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to create trigger');
      }

      return response.data;
    } catch (error) {
      console.error('Error creating SMS trigger:', error);
      throw error;
    }
  },

  /**
   * Update an existing SMS trigger
   */
  updateTrigger: async (triggerId: string, triggerData: Partial<TriggerFormData>, userId: string): Promise<SmsTriggerRule> => {
    try {
      // Use API endpoint for trigger updates
      const response = await apiRequest<TriggerUpdateResponse>({
        method: 'PUT',
        url: `/api/sms/triggers/${triggerId}`,
        data: {
          ...triggerData,
          updated_by: userId
        }
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update trigger');
      }

      return response.data;
    } catch (error) {
      console.error('Error updating SMS trigger:', error);
      throw error;
    }
  },

  /**
   * Toggle trigger active/inactive status
   */
  toggleTrigger: async (triggerId: string, enabled: boolean, userId: string): Promise<SmsTriggerRule> => {
    try {
      // Use API endpoint for toggle operations
      const response = await apiRequest<TriggerUpdateResponse>({
        method: 'POST',
        url: `/api/sms/triggers/${triggerId}/toggle`,
        data: {
          enabled,
          updated_by: userId
        }
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to toggle trigger');
      }

      return response.data;
    } catch (error) {
      console.error('Error toggling SMS trigger:', error);
      throw error;
    }
  },

  /**
   * Delete an SMS trigger
   */
  deleteTrigger: async (triggerId: string): Promise<void> => {
    try {
      // Use API endpoint to ensure proper cleanup of related data
      await apiRequest({
        method: 'DELETE',
        url: `/api/sms/triggers/${triggerId}`
      });
    } catch (error) {
      console.error('Error deleting SMS trigger:', error);
      throw error;
    }
  },

  /**
   * Get trigger execution history
   */
  getTriggerHistory: async (
    organisationId: string,
    triggerId?: string,
    page = 1,
    perPage = 20
  ): Promise<TriggerExecutionHistoryResponse> => {
    try {
      if (!triggerId) {
        throw new Error('Trigger ID is required to fetch history');
      }

      const params = new URLSearchParams({
        organisation_id: organisationId,
        page: page.toString(),
        per_page: perPage.toString()
      });

      const response = await apiRequest<TriggerExecutionHistoryResponse>({
        method: 'GET',
        url: `/api/sms/triggers/${triggerId}/history?${params.toString()}`
      });

      if (!response.success) {
        throw new Error('Failed to fetch trigger history');
      }

      return response;
    } catch (error) {
      console.error('Error fetching trigger history:', error);
      throw error;
    }
  },

  /**
   * Get trigger templates (predefined)
   */
  getTriggerTemplates: async (): Promise<any[]> => {
    try {
      // Fetch templates from the API
      const response = await apiRequest<{ success: boolean; data: any[] }>({
        method: 'GET',
        url: '/api/sms/triggers/templates'
      });

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch trigger templates');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching trigger templates:', error);
      throw error;
    }
  },

  /**
   * Test trigger conditions against customer data
   */
  testTriggerConditions: async (
    organisationId: string,
    conditions: any,
    logic: string
  ): Promise<{ 
    estimated_reach: number; 
    sample_customers: any[] 
  }> => {
    try {
      const response = await apiRequest({
        method: 'POST',
        url: '/api/sms/triggers/test-conditions',
        data: {
          organisation_id: organisationId,
          conditions,
          logic
        }
      });

      return response;
    } catch (error) {
      console.error('Error testing trigger conditions:', error);
      throw error;
    }
  }
};

export default SmsService;